/** @format */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    IBusiness,
    IBusinessMembership,
    IImpersonationContext,
    IQuickBooksConnection,
    ISyncJob,
    ICollectCustomer,
    ICollectInvoice,
    IArAgingSummary,
    IDashboardOverview,
    ICollectionsMetrics,
    IRecoveryFunnel,
    IRecoveryHealth,
    IRecoveryTrends,
    IDunningCampaign,
    IReminderTemplate,
    IDunningEvent,
    IInvoiceTimelineEntry,
    IRecoveryStateMetadata,
    IRecoveryStateTransition,
    IPromiseToPay,
    IRecoveryQueuePage,
    IRecoveryQueueSummary,
    IEscalationDecision,
    ICustomerReply,
    IRepliesSummary,
} from "../../utility/types/collectTypes";
import {
    fetchMyBusinesses,
    createBusiness,
    fetchActiveBusiness,
    updateBusinessSettings,
    startQuickBooksConnect,
    disconnectQuickBooks,
    fetchQuickBooksStatus,
    syncCustomers,
    syncInvoices,
    syncAll,
    fetchSyncJobs,
    fetchCustomers,
    fetchCustomer,
    fetchInvoices,
    fetchInvoice,
    fetchArAging,
    fetchDashboardOverview,
    fetchCollectionsMetrics,
    fetchRecoveryFunnel,
    fetchRecoveryHealth,
    fetchRecoveryTrends,
    fetchCampaigns,
    fetchCampaign,
    createCampaign,
    updateCampaign,
    deleteCampaign,
    fetchTemplates,
    fetchTemplate,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    fetchDunningEvents,
    runDunningNow,
    sendManualReminder,
    fetchInvoiceTimeline,
    fetchRecoveryStateMetadata,
    transitionInvoiceState,
    fetchInvoiceTransitions,
    createInvoicePromise,
    fetchInvoicePromises,
    fetchBusinessPromises,
    markPromiseKept,
    markPromiseBroken,
    cancelInvoicePromise,
    fetchRecoveryQueue,
    fetchRecoveryQueueSummary,
    assignInvoice,
    manualEscalateInvoice,
    fetchReplies,
    fetchRepliesSummary,
    fetchReply,
    reprocessReply,
    resolveReply,
    ingestManualReply,
} from "../actions/collectAction";

// ─────────────────────────────────────────────────────────────────────────
// Collect / AARRP slice.
//
// Domain-grouped state with one isLoading + error per domain so concurrent
// requests don't clobber each other's spinners or error banners.
// ─────────────────────────────────────────────────────────────────────────

interface CollectState {
    // ── Active tenant context ────────────────────────────────────────────
    activeBusinessId: string | null;
    activeBusiness: IBusiness | null;
    activeBusinessRole: string | null;
    activeBusinessRequestId: string | null;
    memberships: IBusinessMembership[];
    isLoadingBusinesses: boolean;
    businessesError: string | null;
    isCreatingBusiness: boolean;
    createBusinessError: string | null;
    isUpdatingBusiness: boolean;
    updateBusinessError: string | null;

    // ── Super-admin console + impersonation ──────────────────────────────
    isPlatformSuperAdmin: boolean;
    impersonation: IImpersonationContext;

    // ── QuickBooks connection ────────────────────────────────────────────
    quickbooks: IQuickBooksConnection | null;
    isLoadingQuickBooks: boolean;
    quickBooksError: string | null;
    quickBooksRequestId: string | null;
    isStartingQbConnect: boolean;
    startQbConnectError: string | null;
    qbConnectAuthUrl: string | null;
    isDisconnectingQb: boolean;
    disconnectQbError: string | null;

    // ── Sync ─────────────────────────────────────────────────────────────
    latestSyncJob: ISyncJob | null;
    syncJobs: ISyncJob[];
    isSyncing: boolean;
    syncError: string | null;
    syncRequestId: string | null;
    isLoadingSyncJobs: boolean;
    syncJobsError: string | null;
    syncJobsRequestId: string | null;

    // ── Customers ────────────────────────────────────────────────────────
    customers: ICollectCustomer[];
    customersCursor: string | null;
    currentCustomer: ICollectCustomer | null;
    isLoadingCustomers: boolean;
    customersError: string | null;

    // ── Invoices ─────────────────────────────────────────────────────────
    invoices: ICollectInvoice[];
    invoicesCursor: string | null;
    currentInvoice: ICollectInvoice | null;
    isLoadingInvoices: boolean;
    invoicesError: string | null;
    invoicesRequestId: string | null;
    arAging: IArAgingSummary | null;
    isLoadingArAging: boolean;
    arAgingError: string | null;
    arAgingRequestId: string | null;
    dashboardOverview: IDashboardOverview | null;
    isLoadingDashboardOverview: boolean;
    dashboardOverviewError: string | null;
    collectionsMetrics: ICollectionsMetrics | null;
    isLoadingCollectionsMetrics: boolean;
    collectionsMetricsError: string | null;
    collectionsMetricsRequestId: string | null;
    recoveryFunnel: IRecoveryFunnel | null;
    isLoadingRecoveryFunnel: boolean;
    recoveryFunnelError: string | null;
    recoveryHealth: IRecoveryHealth | null;
    isLoadingRecoveryHealth: boolean;
    recoveryHealthError: string | null;
    recoveryTrends: IRecoveryTrends | null;
    isLoadingRecoveryTrends: boolean;
    recoveryTrendsError: string | null;

    // ── Campaigns ────────────────────────────────────────────────────────
    campaigns: IDunningCampaign[];
    currentCampaign: IDunningCampaign | null;
    isLoadingCampaigns: boolean;
    campaignsError: string | null;
    isSavingCampaign: boolean;
    campaignSaveError: string | null;

    // ── Templates ────────────────────────────────────────────────────────
    templates: IReminderTemplate[];
    currentTemplate: IReminderTemplate | null;
    isLoadingTemplates: boolean;
    templatesError: string | null;
    isSavingTemplate: boolean;
    templateSaveError: string | null;

    // ── Dunning activity ─────────────────────────────────────────────────
    dunningEvents: IDunningEvent[];
    dunningEventsCursor: string | null;
    isLoadingDunningEvents: boolean;
    dunningEventsError: string | null;
    dunningEventsRequestId: string | null;
    isRunningDunning: boolean;
    runDunningError: string | null;
    runDunningRequestId: string | null;
    lastDunningRun: {
        processed: number;
        sent: number;
        skipped: number;
        failed: number;
    } | null;
    isSendingManualReminder: boolean;
    manualReminderError: string | null;
    lastManualReminder: IDunningEvent | null;

    // ── Invoice timeline ─────────────────────────────────────────────────
    invoiceTimeline: IInvoiceTimelineEntry[];
    isLoadingTimeline: boolean;
    timelineError: string | null;

    // ── Recovery state machine (Stage 2A) ────────────────────────────────
    recoveryStateMetadata: IRecoveryStateMetadata[];
    isLoadingRecoveryStateMetadata: boolean;
    recoveryStateMetadataError: string | null;
    invoiceTransitions: IRecoveryStateTransition[];
    isLoadingInvoiceTransitions: boolean;
    invoiceTransitionsError: string | null;
    isTransitioningInvoiceState: boolean;
    transitionInvoiceStateError: string | null;
    // ── Promise-to-Pay (Stage 2B) ──────────────────────────────
    invoicePromises: IPromiseToPay[];
    isLoadingInvoicePromises: boolean;
    invoicePromisesError: string | null;
    businessPromises: IPromiseToPay[];
    businessPromisesCursor: string | null;
    isLoadingBusinessPromises: boolean;
    businessPromisesError: string | null;
    businessPromisesRequestId: string | null;
    isSavingPromise: boolean;
    promiseSaveError: string | null;

    // ── Recovery Queue + Escalation (Stage 2C) ───────────────────
    recoveryQueue: ICollectInvoice[];
    recoveryQueueCursor: string | null;
    isLoadingRecoveryQueue: boolean;
    recoveryQueueError: string | null;
    recoveryQueueSummary: IRecoveryQueueSummary | null;
    isLoadingRecoveryQueueSummary: boolean;
    recoveryQueueSummaryError: string | null;
    isAssigningInvoice: boolean;
    assignInvoiceError: string | null;
    isManualEscalating: boolean;
    manualEscalateError: string | null;
    lastEscalationDecision: IEscalationDecision | null;

    // ── Customer Replies (Stage 2D) ──────────────────────
    replies: ICustomerReply[];
    repliesCursor: string | null;
    isLoadingReplies: boolean;
    repliesError: string | null;
    currentReply: ICustomerReply | null;
    isLoadingCurrentReply: boolean;
    currentReplyError: string | null;
    repliesSummary: IRepliesSummary | null;
    isLoadingRepliesSummary: boolean;
    repliesSummaryError: string | null;
    isProcessingReply: boolean;
    replyActionError: string | null;
}

const initialState: CollectState = {
    activeBusinessId: null,
    activeBusiness: null,
    activeBusinessRole: null,
    activeBusinessRequestId: null,
    memberships: [],
    isLoadingBusinesses: false,
    businessesError: null,
    isCreatingBusiness: false,
    createBusinessError: null,
    isUpdatingBusiness: false,
    updateBusinessError: null,

    isPlatformSuperAdmin: false,
    impersonation: { active: false, businessId: null, businessName: null },

    quickbooks: null,
    isLoadingQuickBooks: false,
    quickBooksError: null,
    quickBooksRequestId: null,
    isStartingQbConnect: false,
    startQbConnectError: null,
    qbConnectAuthUrl: null,
    isDisconnectingQb: false,
    disconnectQbError: null,

    latestSyncJob: null,
    syncJobs: [],
    isSyncing: false,
    syncError: null,
    syncRequestId: null,
    isLoadingSyncJobs: false,
    syncJobsError: null,
    syncJobsRequestId: null,

    customers: [],
    customersCursor: null,
    currentCustomer: null,
    isLoadingCustomers: false,
    customersError: null,

    invoices: [],
    invoicesCursor: null,
    currentInvoice: null,
    isLoadingInvoices: false,
    invoicesError: null,
    invoicesRequestId: null,
    arAging: null,
    isLoadingArAging: false,
    arAgingError: null,
    arAgingRequestId: null,
    dashboardOverview: null,
    isLoadingDashboardOverview: false,
    dashboardOverviewError: null,
    collectionsMetrics: null,
    isLoadingCollectionsMetrics: false,
    collectionsMetricsError: null,
    collectionsMetricsRequestId: null,
    recoveryFunnel: null,
    isLoadingRecoveryFunnel: false,
    recoveryFunnelError: null,
    recoveryHealth: null,
    isLoadingRecoveryHealth: false,
    recoveryHealthError: null,
    recoveryTrends: null,
    isLoadingRecoveryTrends: false,
    recoveryTrendsError: null,

    campaigns: [],
    currentCampaign: null,
    isLoadingCampaigns: false,
    campaignsError: null,
    isSavingCampaign: false,
    campaignSaveError: null,

    templates: [],
    currentTemplate: null,
    isLoadingTemplates: false,
    templatesError: null,
    isSavingTemplate: false,
    templateSaveError: null,

    dunningEvents: [],
    dunningEventsCursor: null,
    isLoadingDunningEvents: false,
    dunningEventsError: null,
    dunningEventsRequestId: null,
    isRunningDunning: false,
    runDunningError: null,
    runDunningRequestId: null,
    lastDunningRun: null,
    isSendingManualReminder: false,
    manualReminderError: null,
    lastManualReminder: null,

    invoiceTimeline: [],
    isLoadingTimeline: false,
    timelineError: null,

    recoveryStateMetadata: [],
    isLoadingRecoveryStateMetadata: false,
    recoveryStateMetadataError: null,
    invoiceTransitions: [],
    isLoadingInvoiceTransitions: false,
    invoiceTransitionsError: null,
    isTransitioningInvoiceState: false,
    transitionInvoiceStateError: null,

    invoicePromises: [],
    isLoadingInvoicePromises: false,
    invoicePromisesError: null,
    businessPromises: [],
    businessPromisesCursor: null,
    isLoadingBusinessPromises: false,
    businessPromisesError: null,
    businessPromisesRequestId: null,
    isSavingPromise: false,
    promiseSaveError: null,

    recoveryQueue: [],
    recoveryQueueCursor: null,
    isLoadingRecoveryQueue: false,
    recoveryQueueError: null,
    recoveryQueueSummary: null,
    isLoadingRecoveryQueueSummary: false,
    recoveryQueueSummaryError: null,
    isAssigningInvoice: false,
    assignInvoiceError: null,
    isManualEscalating: false,
    manualEscalateError: null,
    lastEscalationDecision: null,

    replies: [],
    repliesCursor: null,
    isLoadingReplies: false,
    repliesError: null,
    currentReply: null,
    isLoadingCurrentReply: false,
    currentReplyError: null,
    repliesSummary: null,
    isLoadingRepliesSummary: false,
    repliesSummaryError: null,
    isProcessingReply: false,
    replyActionError: null,
};

const collectSlice = createSlice({
    name: "collect",
    initialState,
    reducers: {
        setActiveBusiness: (state, action: PayloadAction<string | null>) => {
            state.activeBusinessId = action.payload;
            state.activeBusinessRequestId = null;
            // Wipe domain-scoped caches when tenant changes so we don't show
            // stale data from a different business.
            if (
                state.activeBusiness &&
                state.activeBusiness._id !== action.payload
            ) {
                state.activeBusiness = null;
                state.activeBusinessRole = null;
            }
            state.quickbooks = null;
            state.quickBooksRequestId = null;
            state.isLoadingQuickBooks = false;
            state.quickBooksError = null;
            state.latestSyncJob = null;
            state.syncJobs = [];
            state.syncRequestId = null;
            state.syncJobsRequestId = null;
            state.isSyncing = false;
            state.isLoadingSyncJobs = false;
            state.syncError = null;
            state.syncJobsError = null;
            state.customers = [];
            state.invoices = [];
            state.invoicesCursor = null;
            state.invoicesRequestId = null;
            state.isLoadingInvoices = false;
            state.invoicesError = null;
            state.arAging = null;
            state.arAgingRequestId = null;
            state.isLoadingArAging = false;
            state.arAgingError = null;
            state.collectionsMetrics = null;
            state.collectionsMetricsRequestId = null;
            state.isLoadingCollectionsMetrics = false;
            state.collectionsMetricsError = null;
            state.recoveryFunnel = null;
            state.recoveryHealth = null;
            state.recoveryTrends = null;
            state.campaigns = [];
            state.templates = [];
            state.dunningEvents = [];
            state.dunningEventsCursor = null;
            state.dunningEventsRequestId = null;
            state.isLoadingDunningEvents = false;
            state.dunningEventsError = null;
            state.runDunningRequestId = null;
            state.isRunningDunning = false;
            state.runDunningError = null;
            state.lastDunningRun = null;
            state.invoiceTimeline = [];
            state.invoiceTransitions = [];
            state.invoicePromises = [];
            state.businessPromises = [];
            state.businessPromisesCursor = null;
            state.businessPromisesRequestId = null;
            state.isLoadingBusinessPromises = false;
            state.businessPromisesError = null;
            state.recoveryQueue = [];
            state.recoveryQueueCursor = null;
            state.recoveryQueueSummary = null;
            state.currentInvoice = null;
            state.currentCustomer = null;
            state.currentCampaign = null;
            state.currentTemplate = null;
            state.replies = [];
            state.repliesCursor = null;
            state.repliesSummary = null;
            state.currentReply = null;
        },
        clearCollectErrors: (state) => {
            state.businessesError = null;
            state.createBusinessError = null;
            state.updateBusinessError = null;
            state.quickBooksError = null;
            state.startQbConnectError = null;
            state.disconnectQbError = null;
            state.syncError = null;
            state.syncJobsError = null;
            state.customersError = null;
            state.invoicesError = null;
            state.arAgingError = null;
            state.campaignsError = null;
            state.campaignSaveError = null;
            state.templatesError = null;
            state.templateSaveError = null;
            state.dunningEventsError = null;
            state.runDunningError = null;
            state.manualReminderError = null;
            state.timelineError = null;
            state.recoveryStateMetadataError = null;
            state.invoiceTransitionsError = null;
            state.transitionInvoiceStateError = null;
            state.invoicePromisesError = null;
            state.businessPromisesError = null;
            state.promiseSaveError = null;
            state.recoveryQueueError = null;
            state.recoveryQueueSummaryError = null;
            state.assignInvoiceError = null;
            state.manualEscalateError = null;
        },
        resetCollectState: () => initialState,

        // ── Super-admin console + impersonation ──────────────────────────
        setPlatformSuperAdmin: (state, action: PayloadAction<boolean>) => {
            state.isPlatformSuperAdmin = action.payload;
            // Admins start with no workspace selected; only opening a business
            // from the console (impersonation) populates the switcher. Clear any
            // workspace auto-selected before the admin flag resolved.
            if (action.payload && !state.impersonation.active) {
                state.activeBusinessId = null;
                state.activeBusiness = null;
                state.activeBusinessRole = null;
            }
        },
        /**
         * Enter (impersonate) another business as a super admin. Points the
         * active-tenant context at the target and wipes domain caches so no
         * data from the previous workspace leaks across. The business + role
         * are hydrated by a follow-up `fetchActiveBusiness` dispatch.
         */
        enterImpersonation: (
            state,
            action: PayloadAction<{ businessId: string; businessName: string }>,
        ) => {
            state.impersonation = {
                active: true,
                businessId: action.payload.businessId,
                businessName: action.payload.businessName,
            };
            state.activeBusinessId = action.payload.businessId;
            state.activeBusiness = null;
            state.activeBusinessRole = null;
            state.activeBusinessRequestId = null;
            // Wipe tenant-scoped caches (mirror setActiveBusiness).
            state.quickbooks = null;
            state.quickBooksRequestId = null;
            state.isLoadingQuickBooks = false;
            state.quickBooksError = null;
            state.latestSyncJob = null;
            state.syncJobs = [];
            state.syncRequestId = null;
            state.syncJobsRequestId = null;
            state.isSyncing = false;
            state.isLoadingSyncJobs = false;
            state.syncError = null;
            state.syncJobsError = null;
            state.customers = [];
            state.invoices = [];
            state.invoicesCursor = null;
            state.invoicesRequestId = null;
            state.isLoadingInvoices = false;
            state.invoicesError = null;
            state.arAging = null;
            state.arAgingRequestId = null;
            state.isLoadingArAging = false;
            state.arAgingError = null;
            state.collectionsMetrics = null;
            state.collectionsMetricsRequestId = null;
            state.isLoadingCollectionsMetrics = false;
            state.collectionsMetricsError = null;
            state.recoveryFunnel = null;
            state.recoveryHealth = null;
            state.recoveryTrends = null;
            state.campaigns = [];
            state.templates = [];
            state.dunningEvents = [];
            state.dunningEventsCursor = null;
            state.dunningEventsRequestId = null;
            state.isLoadingDunningEvents = false;
            state.dunningEventsError = null;
            state.runDunningRequestId = null;
            state.isRunningDunning = false;
            state.runDunningError = null;
            state.lastDunningRun = null;
            state.invoiceTimeline = [];
            state.invoiceTransitions = [];
            state.invoicePromises = [];
            state.businessPromises = [];
            state.businessPromisesCursor = null;
            state.businessPromisesRequestId = null;
            state.isLoadingBusinessPromises = false;
            state.businessPromisesError = null;
            state.recoveryQueue = [];
            state.recoveryQueueCursor = null;
            state.recoveryQueueSummary = null;
            state.currentInvoice = null;
            state.currentCustomer = null;
            state.currentCampaign = null;
            state.currentTemplate = null;
            state.replies = [];
            state.repliesCursor = null;
            state.repliesSummary = null;
            state.currentReply = null;
        },
        /**
         * Exit impersonation. Clears the impersonated context and the active
         * business, returning a super admin to an empty workspace switcher
         * until they open another business from the console.
         */
        exitImpersonation: (state) => {
            state.impersonation = {
                active: false,
                businessId: null,
                businessName: null,
            };
            state.activeBusinessId = null;
            state.activeBusiness = null;
            state.activeBusinessRole = null;
            state.activeBusinessRequestId = null;
            state.quickbooks = null;
            state.quickBooksRequestId = null;
            state.isLoadingQuickBooks = false;
            state.quickBooksError = null;
            state.latestSyncJob = null;
            state.syncJobs = [];
            state.syncRequestId = null;
            state.syncJobsRequestId = null;
            state.isSyncing = false;
            state.isLoadingSyncJobs = false;
            state.syncError = null;
            state.syncJobsError = null;
            state.invoices = [];
            state.invoicesCursor = null;
            state.invoicesRequestId = null;
            state.isLoadingInvoices = false;
            state.invoicesError = null;
            state.arAging = null;
            state.arAgingRequestId = null;
            state.isLoadingArAging = false;
            state.arAgingError = null;
            state.dunningEvents = [];
            state.dunningEventsCursor = null;
            state.dunningEventsRequestId = null;
            state.isLoadingDunningEvents = false;
            state.dunningEventsError = null;
            state.runDunningRequestId = null;
            state.isRunningDunning = false;
            state.runDunningError = null;
            state.lastDunningRun = null;
            state.businessPromises = [];
            state.businessPromisesCursor = null;
            state.businessPromisesRequestId = null;
            state.isLoadingBusinessPromises = false;
            state.businessPromisesError = null;
            state.collectionsMetrics = null;
            state.collectionsMetricsRequestId = null;
            state.isLoadingCollectionsMetrics = false;
            state.collectionsMetricsError = null;
        },
    },
    extraReducers: (builder) => {
        // ── Businesses ───────────────────────────────────────────────────
        builder
            .addCase(fetchMyBusinesses.pending, (state) => {
                state.isLoadingBusinesses = true;
                state.businessesError = null;
            })
            .addCase(fetchMyBusinesses.fulfilled, (state, action) => {
                state.isLoadingBusinesses = false;
                const payload = Array.isArray(action.payload) ? action.payload : [];
                state.memberships = payload;
                // While impersonating (super admin viewing a business they are
                // not a member of), never let the membership list re-point or
                // clobber the active tenant — the impersonated context is
                // authoritative and hydrated separately via fetchActiveBusiness.
                if (state.impersonation.active) {
                    return;
                }
                // Default to the first membership if none active yet. Super
                // admins are exempt: their workspace stays empty until they open
                // a business from the admin console.
                if (
                    !state.isPlatformSuperAdmin &&
                    !state.activeBusinessId &&
                    payload.length > 0
                ) {
                    const first = payload[0];
                    state.activeBusinessId = first.businessId;
                    if (first.business) state.activeBusiness = first.business;
                    state.activeBusinessRole = first.role;
                } else if (state.activeBusinessId && payload.length > 0) {
                    // Re-hydrate role/business for the already-active workspace.
                    // Without this, returning to a page after an OAuth round-trip
                    // (e.g. an interrupted QuickBooks connect) can leave the role
                    // null while the id is set, which hides owner/admin actions
                    // like the "Connect QuickBooks" button.
                    const match = payload.find(
                        (m) => m.businessId === state.activeBusinessId,
                    );
                    if (match) {
                        state.activeBusinessRole = match.role;
                        if (match.business) state.activeBusiness = match.business;
                    } else {
                        // Active id no longer in the user's memberships — fall back
                        // to the first membership so the UI stays consistent.
                        const first = payload[0];
                        state.activeBusinessId = first.businessId;
                        if (first.business) state.activeBusiness = first.business;
                        state.activeBusinessRole = first.role;
                    }
                }
            })
            .addCase(fetchMyBusinesses.rejected, (state, action) => {
                state.isLoadingBusinesses = false;
                state.businessesError = action.payload as string;
            });

        builder
            .addCase(createBusiness.pending, (state) => {
                state.isCreatingBusiness = true;
                state.createBusinessError = null;
            })
            .addCase(createBusiness.fulfilled, (state, action) => {
                state.isCreatingBusiness = false;
                // Don't push into memberships directly — refresh elsewhere
                // to include role + populated business fields. But set
                // active business so the next page load resolves correctly.
                state.activeBusinessId = action.payload._id;
                state.activeBusiness = action.payload;
                state.activeBusinessRole = "owner";
            })
            .addCase(createBusiness.rejected, (state, action) => {
                state.isCreatingBusiness = false;
                state.createBusinessError = action.payload as string;
            });

        builder
            .addCase(fetchActiveBusiness.pending, (state, action) => {
                state.businessesError = null;
                state.activeBusinessRequestId = action.meta.requestId;
            })
            .addCase(fetchActiveBusiness.fulfilled, (state, action) => {
                if (state.activeBusinessRequestId !== action.meta.requestId) return;
                state.activeBusinessRequestId = null;
                const returnedBusinessId = action.payload.business?._id;
                if (
                    state.activeBusinessId &&
                    returnedBusinessId !== state.activeBusinessId
                ) {
                    return;
                }
                state.activeBusiness = action.payload.business;
                state.activeBusinessRole = action.payload.role;
                if (returnedBusinessId) {
                    state.activeBusinessId = returnedBusinessId;
                }
            })
            .addCase(fetchActiveBusiness.rejected, (state, action) => {
                if (state.activeBusinessRequestId !== action.meta.requestId) return;
                state.activeBusinessRequestId = null;
                state.businessesError = action.payload as string;
            });

        builder
            .addCase(updateBusinessSettings.pending, (state) => {
                state.isUpdatingBusiness = true;
                state.updateBusinessError = null;
            })
            .addCase(updateBusinessSettings.fulfilled, (state, action) => {
                state.isUpdatingBusiness = false;
                state.activeBusiness = action.payload;
            })
            .addCase(updateBusinessSettings.rejected, (state, action) => {
                state.isUpdatingBusiness = false;
                state.updateBusinessError = action.payload as string;
            });

        // ── QuickBooks ───────────────────────────────────────────────────
        builder
            .addCase(fetchQuickBooksStatus.pending, (state, action) => {
                state.isLoadingQuickBooks = true;
                state.quickBooksError = null;
                state.quickBooksRequestId = action.meta.requestId;
            })
            .addCase(fetchQuickBooksStatus.fulfilled, (state, action) => {
                if (state.quickBooksRequestId !== action.meta.requestId) return;
                state.isLoadingQuickBooks = false;
                state.quickBooksRequestId = null;
                state.quickbooks = action.payload;
            })
            .addCase(fetchQuickBooksStatus.rejected, (state, action) => {
                if (state.quickBooksRequestId !== action.meta.requestId) return;
                state.isLoadingQuickBooks = false;
                state.quickBooksRequestId = null;
                state.quickbooks = null;
                state.quickBooksError = action.payload as string;
            });

        builder
            .addCase(startQuickBooksConnect.pending, (state) => {
                state.isStartingQbConnect = true;
                state.startQbConnectError = null;
                state.qbConnectAuthUrl = null;
            })
            .addCase(startQuickBooksConnect.fulfilled, (state, action) => {
                state.isStartingQbConnect = false;
                state.qbConnectAuthUrl = action.payload.authorizationUrl;
            })
            .addCase(startQuickBooksConnect.rejected, (state, action) => {
                state.isStartingQbConnect = false;
                state.startQbConnectError = action.payload as string;
            });

        builder
            .addCase(disconnectQuickBooks.pending, (state) => {
                state.isDisconnectingQb = true;
                state.disconnectQbError = null;
            })
            .addCase(disconnectQuickBooks.fulfilled, (state) => {
                state.isDisconnectingQb = false;
                if (state.quickbooks) {
                    state.quickbooks.status = "disconnected";
                }
            })
            .addCase(disconnectQuickBooks.rejected, (state, action) => {
                state.isDisconnectingQb = false;
                state.disconnectQbError = action.payload as string;
            });

        // ── Sync ─────────────────────────────────────────────────────────
        const syncPending = (state: CollectState, action: any) => {
            state.isSyncing = true;
            state.syncError = null;
            state.syncRequestId = action.meta.requestId;
        };
        const syncFulfilled = (
            state: CollectState,
            action: { payload: ISyncJob; meta: { requestId: string } },
        ) => {
            if (state.syncRequestId !== action.meta.requestId) return;
            state.isSyncing = false;
            state.syncRequestId = null;
            state.latestSyncJob = action.payload;
        };
        const syncRejected = (
            state: CollectState,
            action: { payload: unknown; meta: { requestId: string } },
        ) => {
            if (state.syncRequestId !== action.meta.requestId) return;
            state.isSyncing = false;
            state.syncRequestId = null;
            state.syncError = action.payload as string;
        };

        builder
            .addCase(syncCustomers.pending, syncPending)
            .addCase(syncCustomers.fulfilled, syncFulfilled)
            .addCase(syncCustomers.rejected, syncRejected);
        builder
            .addCase(syncInvoices.pending, syncPending)
            .addCase(syncInvoices.fulfilled, syncFulfilled)
            .addCase(syncInvoices.rejected, syncRejected);
        builder
            .addCase(syncAll.pending, syncPending)
            .addCase(syncAll.fulfilled, syncFulfilled)
            .addCase(syncAll.rejected, syncRejected);

        builder
            .addCase(fetchSyncJobs.pending, (state, action) => {
                state.isLoadingSyncJobs = true;
                state.syncJobsError = null;
                state.syncJobsRequestId = action.meta.requestId;
            })
            .addCase(fetchSyncJobs.fulfilled, (state, action) => {
                if (state.syncJobsRequestId !== action.meta.requestId) return;
                state.isLoadingSyncJobs = false;
                state.syncJobsRequestId = null;
                state.syncJobs = action.payload;
            })
            .addCase(fetchSyncJobs.rejected, (state, action) => {
                if (state.syncJobsRequestId !== action.meta.requestId) return;
                state.isLoadingSyncJobs = false;
                state.syncJobsRequestId = null;
                state.syncJobs = [];
                state.syncJobsError = action.payload as string;
            });

        // ── Customers ────────────────────────────────────────────────────
        builder
            .addCase(fetchCustomers.pending, (state) => {
                state.isLoadingCustomers = true;
                state.customersError = null;
            })
            .addCase(fetchCustomers.fulfilled, (state, action) => {
                state.isLoadingCustomers = false;
                state.customers = action.payload.items;
                state.customersCursor = action.payload.nextCursor ?? null;
            })
            .addCase(fetchCustomers.rejected, (state, action) => {
                state.isLoadingCustomers = false;
                state.customersError = action.payload as string;
            });

        builder
            .addCase(fetchCustomer.pending, (state) => {
                state.customersError = null;
            })
            .addCase(fetchCustomer.fulfilled, (state, action) => {
                state.currentCustomer = action.payload;
            })
            .addCase(fetchCustomer.rejected, (state, action) => {
                state.customersError = action.payload as string;
            });

        // ── Invoices ─────────────────────────────────────────────────────
        builder
            .addCase(fetchInvoices.pending, (state, action) => {
                state.isLoadingInvoices = true;
                state.invoicesError = null;
                state.invoicesRequestId = action.meta.requestId;
            })
            .addCase(fetchInvoices.fulfilled, (state, action) => {
                if (state.invoicesRequestId !== action.meta.requestId) return;
                state.isLoadingInvoices = false;
                state.invoicesRequestId = null;
                state.invoices = action.payload.items;
                state.invoicesCursor = action.payload.nextCursor ?? null;
            })
            .addCase(fetchInvoices.rejected, (state, action) => {
                if (state.invoicesRequestId !== action.meta.requestId) return;
                state.isLoadingInvoices = false;
                state.invoicesRequestId = null;
                state.invoices = [];
                state.invoicesError = action.payload as string;
            });

        builder
            .addCase(fetchInvoice.pending, (state) => {
                state.invoicesError = null;
            })
            .addCase(fetchInvoice.fulfilled, (state, action) => {
                state.currentInvoice = action.payload;
            })
            .addCase(fetchInvoice.rejected, (state, action) => {
                state.invoicesError = action.payload as string;
            });

        builder
            .addCase(fetchArAging.pending, (state, action) => {
                state.isLoadingArAging = true;
                state.arAgingError = null;
                state.arAgingRequestId = action.meta.requestId;
            })
            .addCase(fetchArAging.fulfilled, (state, action) => {
                if (state.arAgingRequestId !== action.meta.requestId) return;
                state.isLoadingArAging = false;
                state.arAgingRequestId = null;
                state.arAging = action.payload;
            })
            .addCase(fetchArAging.rejected, (state, action) => {
                if (state.arAgingRequestId !== action.meta.requestId) return;
                state.isLoadingArAging = false;
                state.arAgingRequestId = null;
                state.arAging = null;
                state.arAgingError = action.payload as string;
            });

        builder
            .addCase(fetchDashboardOverview.pending, (state) => {
                state.isLoadingDashboardOverview = true;
                state.dashboardOverviewError = null;
            })
            .addCase(fetchDashboardOverview.fulfilled, (state, action) => {
                state.isLoadingDashboardOverview = false;
                state.dashboardOverview = action.payload;
            })
            .addCase(fetchDashboardOverview.rejected, (state, action) => {
                state.isLoadingDashboardOverview = false;
                state.dashboardOverviewError = action.payload as string;
            });

        builder
            .addCase(fetchCollectionsMetrics.pending, (state, action) => {
                state.isLoadingCollectionsMetrics = true;
                state.collectionsMetricsError = null;
                state.collectionsMetrics = null;
                state.collectionsMetricsRequestId = action.meta.requestId;
            })
            .addCase(fetchCollectionsMetrics.fulfilled, (state, action) => {
                if (state.collectionsMetricsRequestId !== action.meta.requestId) {
                    return;
                }
                state.isLoadingCollectionsMetrics = false;
                state.collectionsMetricsRequestId = null;
                state.collectionsMetrics = action.payload;
            })
            .addCase(fetchCollectionsMetrics.rejected, (state, action) => {
                if (state.collectionsMetricsRequestId !== action.meta.requestId) {
                    return;
                }
                state.isLoadingCollectionsMetrics = false;
                state.collectionsMetricsRequestId = null;
                state.collectionsMetrics = null;
                state.collectionsMetricsError = action.payload as string;
            });

        builder
            .addCase(fetchRecoveryFunnel.pending, (state) => {
                state.isLoadingRecoveryFunnel = true;
                state.recoveryFunnelError = null;
            })
            .addCase(fetchRecoveryFunnel.fulfilled, (state, action) => {
                state.isLoadingRecoveryFunnel = false;
                state.recoveryFunnel = action.payload;
            })
            .addCase(fetchRecoveryFunnel.rejected, (state, action) => {
                state.isLoadingRecoveryFunnel = false;
                state.recoveryFunnelError = action.payload as string;
            });

        builder
            .addCase(fetchRecoveryHealth.pending, (state) => {
                state.isLoadingRecoveryHealth = true;
                state.recoveryHealthError = null;
            })
            .addCase(fetchRecoveryHealth.fulfilled, (state, action) => {
                state.isLoadingRecoveryHealth = false;
                state.recoveryHealth = action.payload;
            })
            .addCase(fetchRecoveryHealth.rejected, (state, action) => {
                state.isLoadingRecoveryHealth = false;
                state.recoveryHealthError = action.payload as string;
            });

        builder
            .addCase(fetchRecoveryTrends.pending, (state) => {
                state.isLoadingRecoveryTrends = true;
                state.recoveryTrendsError = null;
            })
            .addCase(fetchRecoveryTrends.fulfilled, (state, action) => {
                state.isLoadingRecoveryTrends = false;
                state.recoveryTrends = action.payload;
            })
            .addCase(fetchRecoveryTrends.rejected, (state, action) => {
                state.isLoadingRecoveryTrends = false;
                state.recoveryTrendsError = action.payload as string;
            });

        // ── Campaigns ────────────────────────────────────────────────────
        builder
            .addCase(fetchCampaigns.pending, (state) => {
                state.isLoadingCampaigns = true;
                state.campaignsError = null;
            })
            .addCase(fetchCampaigns.fulfilled, (state, action) => {
                state.isLoadingCampaigns = false;
                state.campaigns = action.payload;
            })
            .addCase(fetchCampaigns.rejected, (state, action) => {
                state.isLoadingCampaigns = false;
                state.campaignsError = action.payload as string;
            });

        builder
            .addCase(fetchCampaign.fulfilled, (state, action) => {
                state.currentCampaign = action.payload;
            })
            .addCase(fetchCampaign.rejected, (state, action) => {
                state.campaignsError = action.payload as string;
            });

        const campaignSavePending = (state: CollectState) => {
            state.isSavingCampaign = true;
            state.campaignSaveError = null;
        };
        const campaignSaveRejected = (
            state: CollectState,
            action: { payload: unknown },
        ) => {
            state.isSavingCampaign = false;
            state.campaignSaveError = action.payload as string;
        };

        builder
            .addCase(createCampaign.pending, campaignSavePending)
            .addCase(createCampaign.fulfilled, (state, action) => {
                state.isSavingCampaign = false;
                state.campaigns = [...state.campaigns, action.payload];
                state.currentCampaign = action.payload;
            })
            .addCase(createCampaign.rejected, campaignSaveRejected);

        builder
            .addCase(updateCampaign.pending, campaignSavePending)
            .addCase(updateCampaign.fulfilled, (state, action) => {
                state.isSavingCampaign = false;
                state.campaigns = state.campaigns.map((c) =>
                    c._id === action.payload._id ? action.payload : c,
                );
                state.currentCampaign = action.payload;
            })
            .addCase(updateCampaign.rejected, campaignSaveRejected);

        builder
            .addCase(deleteCampaign.pending, campaignSavePending)
            .addCase(deleteCampaign.fulfilled, (state, action) => {
                state.isSavingCampaign = false;
                state.campaigns = state.campaigns.filter(
                    (c) => c._id !== action.payload,
                );
                if (state.currentCampaign?._id === action.payload) {
                    state.currentCampaign = null;
                }
            })
            .addCase(deleteCampaign.rejected, campaignSaveRejected);

        // ── Templates ────────────────────────────────────────────────────
        builder
            .addCase(fetchTemplates.pending, (state) => {
                state.isLoadingTemplates = true;
                state.templatesError = null;
            })
            .addCase(fetchTemplates.fulfilled, (state, action) => {
                state.isLoadingTemplates = false;
                state.templates = action.payload;
            })
            .addCase(fetchTemplates.rejected, (state, action) => {
                state.isLoadingTemplates = false;
                state.templatesError = action.payload as string;
            });

        builder
            .addCase(fetchTemplate.fulfilled, (state, action) => {
                state.currentTemplate = action.payload;
            })
            .addCase(fetchTemplate.rejected, (state, action) => {
                state.templatesError = action.payload as string;
            });

        const templateSavePending = (state: CollectState) => {
            state.isSavingTemplate = true;
            state.templateSaveError = null;
        };
        const templateSaveRejected = (
            state: CollectState,
            action: { payload: unknown },
        ) => {
            state.isSavingTemplate = false;
            state.templateSaveError = action.payload as string;
        };

        builder
            .addCase(createTemplate.pending, templateSavePending)
            .addCase(createTemplate.fulfilled, (state, action) => {
                state.isSavingTemplate = false;
                state.templates = [...state.templates, action.payload];
                state.currentTemplate = action.payload;
            })
            .addCase(createTemplate.rejected, templateSaveRejected);

        builder
            .addCase(updateTemplate.pending, templateSavePending)
            .addCase(updateTemplate.fulfilled, (state, action) => {
                state.isSavingTemplate = false;
                state.templates = state.templates.map((t) =>
                    t._id === action.payload._id ? action.payload : t,
                );
                state.currentTemplate = action.payload;
            })
            .addCase(updateTemplate.rejected, templateSaveRejected);

        builder
            .addCase(deleteTemplate.pending, templateSavePending)
            .addCase(deleteTemplate.fulfilled, (state, action) => {
                state.isSavingTemplate = false;
                state.templates = state.templates.filter(
                    (t) => t._id !== action.payload,
                );
                if (state.currentTemplate?._id === action.payload) {
                    state.currentTemplate = null;
                }
            })
            .addCase(deleteTemplate.rejected, templateSaveRejected);

        // ── Dunning activity + manual actions ────────────────────────────
        builder
            .addCase(fetchDunningEvents.pending, (state, action) => {
                state.isLoadingDunningEvents = true;
                state.dunningEventsError = null;
                state.dunningEventsRequestId = action.meta.requestId;
            })
            .addCase(fetchDunningEvents.fulfilled, (state, action) => {
                if (state.dunningEventsRequestId !== action.meta.requestId) return;
                state.isLoadingDunningEvents = false;
                state.dunningEventsRequestId = null;
                state.dunningEvents = action.payload.items;
                state.dunningEventsCursor = action.payload.nextCursor ?? null;
            })
            .addCase(fetchDunningEvents.rejected, (state, action) => {
                if (state.dunningEventsRequestId !== action.meta.requestId) return;
                state.isLoadingDunningEvents = false;
                state.dunningEventsRequestId = null;
                state.dunningEvents = [];
                state.dunningEventsError = action.payload as string;
            });

        builder
            .addCase(runDunningNow.pending, (state, action) => {
                state.isRunningDunning = true;
                state.runDunningError = null;
                state.runDunningRequestId = action.meta.requestId;
            })
            .addCase(runDunningNow.fulfilled, (state, action) => {
                if (state.runDunningRequestId !== action.meta.requestId) return;
                state.isRunningDunning = false;
                state.runDunningRequestId = null;
                state.lastDunningRun = action.payload;
            })
            .addCase(runDunningNow.rejected, (state, action) => {
                if (state.runDunningRequestId !== action.meta.requestId) return;
                state.isRunningDunning = false;
                state.runDunningRequestId = null;
                state.runDunningError = action.payload as string;
            });

        builder
            .addCase(sendManualReminder.pending, (state) => {
                state.isSendingManualReminder = true;
                state.manualReminderError = null;
            })
            .addCase(sendManualReminder.fulfilled, (state, action) => {
                state.isSendingManualReminder = false;
                state.lastManualReminder = action.payload;
                // Prepend the event to the live timeline if we have one.
                state.invoiceTimeline = [
                    { kind: "dunning", occurredAt: action.payload.sentAt, data: action.payload },
                    ...state.invoiceTimeline,
                ];
            })
            .addCase(sendManualReminder.rejected, (state, action) => {
                state.isSendingManualReminder = false;
                state.manualReminderError = action.payload as string;
            });

        builder
            .addCase(fetchInvoiceTimeline.pending, (state) => {
                state.isLoadingTimeline = true;
                state.timelineError = null;
            })
            .addCase(fetchInvoiceTimeline.fulfilled, (state, action) => {
                state.isLoadingTimeline = false;
                state.invoiceTimeline = action.payload;
            })
            .addCase(fetchInvoiceTimeline.rejected, (state, action) => {
                state.isLoadingTimeline = false;
                state.timelineError = action.payload as string;
            });

        // ── Recovery state machine (Stage 2A) ────────────────────────────
        builder
            .addCase(fetchRecoveryStateMetadata.pending, (state) => {
                state.isLoadingRecoveryStateMetadata = true;
                state.recoveryStateMetadataError = null;
            })
            .addCase(fetchRecoveryStateMetadata.fulfilled, (state, action) => {
                state.isLoadingRecoveryStateMetadata = false;
                state.recoveryStateMetadata = action.payload;
            })
            .addCase(fetchRecoveryStateMetadata.rejected, (state, action) => {
                state.isLoadingRecoveryStateMetadata = false;
                state.recoveryStateMetadataError = action.payload as string;
            });

        builder
            .addCase(fetchInvoiceTransitions.pending, (state) => {
                state.isLoadingInvoiceTransitions = true;
                state.invoiceTransitionsError = null;
            })
            .addCase(fetchInvoiceTransitions.fulfilled, (state, action) => {
                state.isLoadingInvoiceTransitions = false;
                state.invoiceTransitions = action.payload;
            })
            .addCase(fetchInvoiceTransitions.rejected, (state, action) => {
                state.isLoadingInvoiceTransitions = false;
                state.invoiceTransitionsError = action.payload as string;
            });

        builder
            .addCase(transitionInvoiceState.pending, (state) => {
                state.isTransitioningInvoiceState = true;
                state.transitionInvoiceStateError = null;
            })
            .addCase(transitionInvoiceState.fulfilled, (state, action) => {
                state.isTransitioningInvoiceState = false;
                // Optimistically update the current invoice's recoveryState
                // so the chip flips immediately. The follow-up fetches will
                // bring authoritative server data (transitions list + invoice).
                if (
                    state.currentInvoice &&
                    state.currentInvoice._id === action.payload.invoiceId &&
                    action.payload.transitioned
                ) {
                    state.currentInvoice.recoveryState =
                        action.payload.to as any;
                    state.currentInvoice.recoveryStateEnteredAt =
                        new Date().toISOString();
                }
            })
            .addCase(transitionInvoiceState.rejected, (state, action) => {
                state.isTransitioningInvoiceState = false;
                state.transitionInvoiceStateError = action.payload as string;
            })

            // ── Promise-to-Pay (Stage 2B) ──────────────────────────────
            .addCase(fetchInvoicePromises.pending, (state) => {
                state.isLoadingInvoicePromises = true;
                state.invoicePromisesError = null;
            })
            .addCase(fetchInvoicePromises.fulfilled, (state, action) => {
                state.isLoadingInvoicePromises = false;
                state.invoicePromises = (action.payload as IPromiseToPay[]) || [];
            })
            .addCase(fetchInvoicePromises.rejected, (state, action) => {
                state.isLoadingInvoicePromises = false;
                state.invoicePromisesError = action.payload as string;
            })
            .addCase(fetchBusinessPromises.pending, (state, action) => {
                state.isLoadingBusinessPromises = true;
                state.businessPromisesError = null;
                state.businessPromisesRequestId = action.meta.requestId;
            })
            .addCase(fetchBusinessPromises.fulfilled, (state, action: any) => {
                if (state.businessPromisesRequestId !== action.meta.requestId) return;
                state.isLoadingBusinessPromises = false;
                state.businessPromisesRequestId = null;
                const payload = action.payload || {};
                state.businessPromises = payload.items || payload.promises || [];
                state.businessPromisesCursor = payload.nextCursor || null;
            })
            .addCase(fetchBusinessPromises.rejected, (state, action) => {
                if (state.businessPromisesRequestId !== action.meta.requestId) return;
                state.isLoadingBusinessPromises = false;
                state.businessPromisesRequestId = null;
                state.businessPromises = [];
                state.businessPromisesError = action.payload as string;
            })
            .addCase(createInvoicePromise.pending, (state) => {
                state.isSavingPromise = true;
                state.promiseSaveError = null;
            })
            .addCase(createInvoicePromise.fulfilled, (state, action) => {
                state.isSavingPromise = false;
                const p = action.payload as IPromiseToPay;
                if (p) state.invoicePromises = [p, ...state.invoicePromises];
            })
            .addCase(createInvoicePromise.rejected, (state, action) => {
                state.isSavingPromise = false;
                state.promiseSaveError = action.payload as string;
            })
            .addCase(markPromiseKept.pending, (state) => {
                state.isSavingPromise = true;
                state.promiseSaveError = null;
            })
            .addCase(markPromiseKept.fulfilled, (state, action) => {
                state.isSavingPromise = false;
                const p = action.payload as IPromiseToPay;
                if (p?._id) {
                    state.invoicePromises = state.invoicePromises.map((x) =>
                        x._id === p._id ? p : x,
                    );
                }
            })
            .addCase(markPromiseKept.rejected, (state, action) => {
                state.isSavingPromise = false;
                state.promiseSaveError = action.payload as string;
            })
            .addCase(markPromiseBroken.pending, (state) => {
                state.isSavingPromise = true;
                state.promiseSaveError = null;
            })
            .addCase(markPromiseBroken.fulfilled, (state, action) => {
                state.isSavingPromise = false;
                const p = action.payload as IPromiseToPay;
                if (p?._id) {
                    state.invoicePromises = state.invoicePromises.map((x) =>
                        x._id === p._id ? p : x,
                    );
                }
            })
            .addCase(markPromiseBroken.rejected, (state, action) => {
                state.isSavingPromise = false;
                state.promiseSaveError = action.payload as string;
            })
            .addCase(cancelInvoicePromise.pending, (state) => {
                state.isSavingPromise = true;
                state.promiseSaveError = null;
            })
            .addCase(cancelInvoicePromise.fulfilled, (state, action) => {
                state.isSavingPromise = false;
                const p = action.payload as IPromiseToPay;
                if (p?._id) {
                    state.invoicePromises = state.invoicePromises.map((x) =>
                        x._id === p._id ? p : x,
                    );
                }
            })
            .addCase(cancelInvoicePromise.rejected, (state, action) => {
                state.isSavingPromise = false;
                state.promiseSaveError = action.payload as string;
            })

            // ── Recovery Queue + Escalation (Stage 2C) ──────────────
            .addCase(fetchRecoveryQueue.pending, (state, action) => {
                state.isLoadingRecoveryQueue = true;
                state.recoveryQueueError = null;
                // If this is a paginated load (cursor set), keep existing items.
                if (!(action.meta.arg as any)?.cursor) {
                    state.recoveryQueue = [];
                    state.recoveryQueueCursor = null;
                }
            })
            .addCase(fetchRecoveryQueue.fulfilled, (state, action) => {
                state.isLoadingRecoveryQueue = false;
                const page = action.payload as IRecoveryQueuePage;
                const isAppend = Boolean((action.meta.arg as any)?.cursor);
                state.recoveryQueue = isAppend
                    ? [...state.recoveryQueue, ...(page.items || [])]
                    : (page.items || []);
                state.recoveryQueueCursor = page.nextCursor || null;
            })
            .addCase(fetchRecoveryQueue.rejected, (state, action) => {
                state.isLoadingRecoveryQueue = false;
                state.recoveryQueueError = action.payload as string;
            })
            .addCase(fetchRecoveryQueueSummary.pending, (state) => {
                state.isLoadingRecoveryQueueSummary = true;
                state.recoveryQueueSummaryError = null;
            })
            .addCase(fetchRecoveryQueueSummary.fulfilled, (state, action) => {
                state.isLoadingRecoveryQueueSummary = false;
                state.recoveryQueueSummary =
                    action.payload as IRecoveryQueueSummary;
            })
            .addCase(fetchRecoveryQueueSummary.rejected, (state, action) => {
                state.isLoadingRecoveryQueueSummary = false;
                state.recoveryQueueSummaryError = action.payload as string;
            })
            .addCase(assignInvoice.pending, (state) => {
                state.isAssigningInvoice = true;
                state.assignInvoiceError = null;
            })
            .addCase(assignInvoice.fulfilled, (state, action) => {
                state.isAssigningInvoice = false;
                const updated = action.payload as ICollectInvoice;
                if (updated?._id) {
                    state.recoveryQueue = state.recoveryQueue.map((inv) =>
                        inv._id === updated._id ? updated : inv,
                    );
                    if (state.currentInvoice?._id === updated._id) {
                        state.currentInvoice = updated;
                    }
                }
            })
            .addCase(assignInvoice.rejected, (state, action) => {
                state.isAssigningInvoice = false;
                state.assignInvoiceError = action.payload as string;
            })
            .addCase(manualEscalateInvoice.pending, (state) => {
                state.isManualEscalating = true;
                state.manualEscalateError = null;
            })
            .addCase(manualEscalateInvoice.fulfilled, (state, action) => {
                state.isManualEscalating = false;
                state.lastEscalationDecision =
                    action.payload as IEscalationDecision;
            })
            .addCase(manualEscalateInvoice.rejected, (state, action) => {
                state.isManualEscalating = false;
                state.manualEscalateError = action.payload as string;
            })

            // ── Stage 2D — Customer Replies ─────────────────────────
            .addCase(fetchReplies.pending, (state, action) => {
                state.isLoadingReplies = true;
                state.repliesError = null;
                if (!(action.meta.arg as any)?.append) {
                    state.replies = [];
                    state.repliesCursor = null;
                }
            })
            .addCase(fetchReplies.fulfilled, (state, action) => {
                state.isLoadingReplies = false;
                const { page, append } = action.payload as {
                    page: { items: ICustomerReply[]; nextCursor: string | null };
                    append: boolean;
                };
                state.replies = append
                    ? [...state.replies, ...(page.items || [])]
                    : page.items || [];
                state.repliesCursor = page.nextCursor || null;
            })
            .addCase(fetchReplies.rejected, (state, action) => {
                state.isLoadingReplies = false;
                state.repliesError = action.payload as string;
            })

            .addCase(fetchRepliesSummary.pending, (state) => {
                state.isLoadingRepliesSummary = true;
                state.repliesSummaryError = null;
            })
            .addCase(fetchRepliesSummary.fulfilled, (state, action) => {
                state.isLoadingRepliesSummary = false;
                state.repliesSummary = action.payload as IRepliesSummary;
            })
            .addCase(fetchRepliesSummary.rejected, (state, action) => {
                state.isLoadingRepliesSummary = false;
                state.repliesSummaryError = action.payload as string;
            })

            .addCase(fetchReply.pending, (state) => {
                state.isLoadingCurrentReply = true;
                state.currentReplyError = null;
            })
            .addCase(fetchReply.fulfilled, (state, action) => {
                state.isLoadingCurrentReply = false;
                state.currentReply = action.payload as ICustomerReply;
            })
            .addCase(fetchReply.rejected, (state, action) => {
                state.isLoadingCurrentReply = false;
                state.currentReplyError = action.payload as string;
            })

            .addCase(reprocessReply.pending, (state) => {
                state.isProcessingReply = true;
                state.replyActionError = null;
            })
            .addCase(reprocessReply.fulfilled, (state, action) => {
                state.isProcessingReply = false;
                const updated = action.payload as ICustomerReply;
                if (updated?._id) {
                    state.replies = state.replies.map((r) =>
                        r._id === updated._id ? updated : r,
                    );
                    if (state.currentReply?._id === updated._id) {
                        state.currentReply = updated;
                    }
                }
            })
            .addCase(reprocessReply.rejected, (state, action) => {
                state.isProcessingReply = false;
                state.replyActionError = action.payload as string;
            })

            .addCase(resolveReply.pending, (state) => {
                state.isProcessingReply = true;
                state.replyActionError = null;
            })
            .addCase(resolveReply.fulfilled, (state, action) => {
                state.isProcessingReply = false;
                const updated = action.payload as ICustomerReply;
                if (updated?._id) {
                    state.replies = state.replies.map((r) =>
                        r._id === updated._id ? updated : r,
                    );
                    if (state.currentReply?._id === updated._id) {
                        state.currentReply = updated;
                    }
                }
            })
            .addCase(resolveReply.rejected, (state, action) => {
                state.isProcessingReply = false;
                state.replyActionError = action.payload as string;
            })

            .addCase(ingestManualReply.pending, (state) => {
                state.isProcessingReply = true;
                state.replyActionError = null;
            })
            .addCase(ingestManualReply.fulfilled, (state, action) => {
                state.isProcessingReply = false;
                const created = action.payload as ICustomerReply;
                if (created?._id) {
                    // Insert at head (most recent first).
                    state.replies = [created, ...state.replies];
                }
            })
            .addCase(ingestManualReply.rejected, (state, action) => {
                state.isProcessingReply = false;
                state.replyActionError = action.payload as string;
            });
    },
});

export const {
    setActiveBusiness,
    clearCollectErrors,
    resetCollectState,
    setPlatformSuperAdmin,
    enterImpersonation,
    exitImpersonation,
} = collectSlice.actions;
export default collectSlice.reducer;
